import { TextField } from "@mui/material";
import dayjs from "dayjs";
import { Controller, type UseControllerProps, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { SchaefflerFormTextFieldDTO } from "~/CMSComponents/dto";
import DateInput from "~/common/components/DateInput";
import { EMAIL_PATTERN } from "~/utils/constants";
import { PHONE_REGEXP } from "~/utils/validations";
import { useResolveValue } from "./schaefflerFormHelper";
import { makeExternallyStyledElement } from "~/CMSComponents/CMSExternalStyleHelper";

export type SchaefflerFormTextFieldProps = Omit<SchaefflerFormTextFieldDTO, "type"> & {
  className?: string;
};

const DATE_FORMAT = "DD.MM.YYYY";

export const SchaefflerFormTextField = (props: SchaefflerFormTextFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const rules: UseControllerProps["rules"] = {};

  const initialValue = useResolveValue(props.initialValue ?? "");

  if (props.required) {
    rules.required = {
      value: props.required,
      message: t("page.scandmc.form.error.required"),
    };
  }

  if (props.maxLength) {
    rules.maxLength = {
      value: props.maxLength,
      message: t("generic.error.tooLong", { maxLength: props.maxLength }),
    };
  }

  if (props.fieldType === "DATE") {
    return (
      <Controller
        control={control}
        name={props.name}
        rules={rules}
        defaultValue={initialValue}
        render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
          <ExternallyStyledDateInput
            rootStyles={props.rootStyles}
            label={t(props.title)}
            date={value ? dayjs(value, DATE_FORMAT).toDate() : value}
            onChange={(date) => {
              onChange(date ? dayjs(date).format(DATE_FORMAT) : "");
            }}
            {...field}
            isInvalid={!!error}
            helperLabel={error?.message}
          />
        )}
      />
    );
  }

  const multiline = props.fieldType === "TEXTAREA";
  let inputMode: "text" | "numeric" | "email" | "tel" | undefined = undefined;

  if (props.fieldType === "ALPHANUMERIC") {
    rules.pattern = {
      value: /^[a-zA-Z0-9]*$/,
      message: "This field can only contain letters and numbers",
    };
  }

  if (props.fieldType === "NUMERIC") {
    rules.pattern = {
      value: /^[0-9]*$/,
      message: "This field can only contain numbers",
    };
    inputMode = "numeric";
  }

  if (props.fieldType === "EMAIL") {
    rules.pattern = {
      value: EMAIL_PATTERN,
      message: "This field must be a valid email address",
    };
    inputMode = "email";
  }

  if (props.fieldType === "PHONE") {
    rules.pattern = {
      value: PHONE_REGEXP,
      message: "This field must be a valid phone number",
    };
    inputMode = "tel";
  }

  return (
    <Controller
      control={control}
      name={props.name}
      defaultValue={initialValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <ExternallyStyledTextField
          rootStyles={props.rootStyles}
          required={props.required}
          label={t(props.title)}
          {...field}
          error={!!error}
          helperText={error?.message}
          multiline={multiline}
          inputMode={inputMode}
          minRows={3}
        />
      )}
    />
  );
};

const ExternallyStyledTextField = makeExternallyStyledElement(TextField);

const ExternallyStyledDateInput = makeExternallyStyledElement(DateInput);
